import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  flexRender,
  type Row,
  type SortingState,
  type Table,
} from "@tanstack/react-table";
import { Search } from "lucide-react";
import {
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";

/** React `data-*` attrs for tables (e.g. tests) — widen so strict HTML typing accepts them. */
type MattersStyleTableElementProps = HTMLAttributes<HTMLTableElement> & {
  "data-testid"?: string;
  "data-table-name"?: string;
};

import { Input, LoadingSpinner, Text } from "@/components/ui";
import { DataTablePagination } from "@/components/ui/shadcn/DataTable/DataTablePagination";
import { DataTableVisibilityDropdown } from "@/components/ui/shadcn/DataTable/DataTableVisibilityDropdown";
import { DraggableCardTableColumnHeader } from "@/components/ui/shadcn/DraggableCardTable/DraggableCardTableColumnHeader";
import { cn } from "@/lib/utils/cn";

import {
  MattersStyleDraggableHeader,
  type MattersStyleColumnMeta,
} from "./MattersStyleDraggableHeader";

// Keeps vertical auto-scroll effectively disabled while allowing horizontal edge scrolling.
const HEADER_DRAG_AUTO_SCROLL = { threshold: { x: 0.2, y: 0.001 } };

export const MATTERS_STYLE_TABLE_PAGE_SIZE_OPTIONS = [10, 20, 30, 50] as const;

export type MattersStyleTableSearchConfig = {
  maxLength?: number;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
};

function bodyColSpanForTable<TData>(
  table: Table<TData>,
  columnIdCountBaseline: number,
) {
  const headers = table.getHeaderGroups().at(0)?.headers ?? [];
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  return Math.max(columnIdCountBaseline, headers.length, visibleColumnCount, 1);
}

/** Default DnD sensors for column drag-reorder (matches My Matters / Cost Categories). */
export function useMattersStyleTableColumnDragSensors() {
  return useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );
}

export type MattersStyleTableShellProps<TData> = {
  table: Table<TData>;
  columnOrder: string[];
  /** Include full column id list length so loading rows span every column before visibility resolves. */
  columnIdCountBaseline: number;
  onColumnDragEnd: (event: DragEndEvent) => void;
  sensors: ReturnType<typeof useMattersStyleTableColumnDragSensors>;

  search: MattersStyleTableSearchConfig;
  /** Renders directly beside the search field. */
  searchActions?: ReactNode;
  /** Renders on the right side of the top toolbar (next to Columns). */
  toolbarTrailing?: ReactNode;
  /** Renders below the search/columns toolbar (e.g. status tabs). */
  toolbarAfter?: ReactNode;

  isLoading?: boolean;
  /** Shows a loading overlay while retaining the currently displayed server page. */
  isFetching?: boolean;
  isError?: boolean;
  errorMessage?: string;
  emptyMessage: string;

  /** Total rows after client filters (for “Showing X–Y of Z”). */
  totalFilteredRowCount: number;
  entityNamePlural: string;
  pageSizeOptions?: readonly number[];

  /** Restores horizontal scroll after sort changes the layout. */
  sorting: SortingState;

  tableProps?: MattersStyleTableElementProps;

  getTableRowProps?: (
    row: Row<TData>,
  ) => Omit<HTMLAttributes<HTMLTableRowElement>, "children">;
  renderContextMenu?: (row: Row<TData>, trigger: ReactNode) => ReactNode;
  /** Constrains the table body to its parent and enables internal vertical scrolling. */
  scrollableBody?: boolean;
  /** Uses TanStack column sizes to keep the table stable while data changes. */
  fixedColumnWidths?: boolean;
};

/**
 * Shared card + toolbar + draggable column headers + body + pagination footer
 * used by My Matters dashboard and Cost Categories (matters settings).
 */
export function MattersStyleTableShell<TData>({
  table,
  columnOrder,
  columnIdCountBaseline,
  onColumnDragEnd,
  sensors,
  search,
  searchActions,
  toolbarTrailing,
  toolbarAfter,
  isLoading,
  isFetching = false,
  isError,
  errorMessage,
  emptyMessage,
  totalFilteredRowCount,
  entityNamePlural,
  pageSizeOptions = MATTERS_STYLE_TABLE_PAGE_SIZE_OPTIONS,
  sorting,
  tableProps,
  getTableRowProps,
  renderContextMenu,
  scrollableBody = false,
  fixedColumnWidths = false,
}: MattersStyleTableShellProps<TData>) {
  const { className: tableClassName, ...restTableProps } = tableProps ?? {};
  const tableHeaders = table.getHeaderGroups().at(0)?.headers ?? [];
  const bodyColSpan = bodyColSpanForTable(table, columnIdCountBaseline);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollLeftRef = useRef(0);
  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (el) {
      el.scrollLeft = tableScrollLeftRef.current;
    }
  }, [sorting]);

  return (
    <div
      className={cn(
        "border-border bg-card min-w-0 rounded-xl border shadow-xs",
        scrollableBody && "flex max-h-full min-h-0 flex-col overflow-hidden",
      )}
    >
      <div className="border-border bg-muted/30 flex min-w-0 items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="relative w-full max-w-full min-w-0 sm:max-w-80">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              maxLength={search.maxLength}
              value={search.value}
              onChange={(event) => {
                search.onChange(event.target.value);
              }}
              className="bg-card h-10 pl-9"
              placeholder={search.placeholder}
            />
          </div>
          {searchActions ? searchActions : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <DataTableVisibilityDropdown table={table} />
          {toolbarTrailing ? toolbarTrailing : null}
        </div>
      </div>

      {toolbarAfter ? toolbarAfter : null}

      <div
        className={cn(
          "relative",
          scrollableBody && "flex min-h-0 flex-1 flex-col",
        )}
      >
        {isFetching && !isLoading ? (
          <div className="bg-card/75 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm">
            <LoadingSpinner />
          </div>
        ) : null}
        <div
          ref={tableScrollRef}
          onScroll={(event) => {
            tableScrollLeftRef.current = event.currentTarget.scrollLeft;
          }}
          className={cn(
            "w-full min-w-0 overflow-x-auto overscroll-x-contain",
            scrollableBody &&
              "min-h-0 flex-1 overflow-y-auto overscroll-contain",
          )}
        >
          <DndContext
            autoScroll={HEADER_DRAG_AUTO_SCROLL}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={onColumnDragEnd}
            sensors={sensors}
          >
            <table
              className={cn(
                "min-w-full",
                fixedColumnWidths ? "table-fixed" : "w-max",
                tableClassName,
              )}
              style={
                fixedColumnWidths ? { width: table.getTotalSize() } : undefined
              }
              {...restTableProps}
            >
              <thead className="bg-muted/40">
                <tr className="border-border border-b">
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {tableHeaders.map((header) =>
                      fixedColumnWidths ? (
                        <DraggableCardTableColumnHeader
                          key={header.id}
                          header={header}
                          sticky={scrollableBody}
                          fixedColumnWidths
                        />
                      ) : (
                        <MattersStyleDraggableHeader
                          key={header.id}
                          header={header}
                          sticky={scrollableBody}
                        />
                      ),
                    )}
                  </SortableContext>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={bodyColSpan} className="p-0 align-middle">
                      <div className="flex min-h-[7rem] w-full items-center justify-center py-10">
                        <LoadingSpinner />
                      </div>
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan={bodyColSpan} className="px-4 py-8 text-center">
                      <Text variant="caption" color="destructive">
                        {errorMessage}
                      </Text>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={bodyColSpan} className="p-0 align-middle">
                      <div className="text-foreground/70 sticky left-0 flex min-h-[7rem] w-screen items-center justify-center px-4 py-8 text-center">
                        <Text variant="subHeading">{emptyMessage}</Text>
                      </div>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    const rowProps = getTableRowProps?.(row) ?? {};
                    const trigger = (
                      <tr
                        key={row.id}
                        {...rowProps}
                        className={cn(
                          "border-border hover:bg-muted/20 border-b",
                          rowProps.className,
                        )}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={
                              fixedColumnWidths
                                ? {
                                    minWidth: cell.column.getSize(),
                                    width: cell.column.getSize(),
                                    maxWidth: cell.column.getSize(),
                                  }
                                : undefined
                            }
                            className={cn(
                              "py-3 pr-4 pl-10 align-middle",
                              (
                                cell.column.columnDef.meta as
                                  | MattersStyleColumnMeta
                                  | undefined
                              )?.cellClassName,
                            )}
                          >
                            <div
                              className={cn(
                                fixedColumnWidths && "min-w-0 truncate",
                              )}
                            >
                              <Text
                                variant={
                                  cell.column.getCanHide()
                                    ? "subHeading"
                                    : "body"
                                }
                                className={cn(
                                  !cell.column.getCanHide() && "font-semibold",
                                )}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </Text>
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                    return renderContextMenu
                      ? renderContextMenu(row, trigger)
                      : trigger;
                  })
                )}
              </tbody>
            </table>
          </DndContext>
        </div>
      </div>

      <div className="border-border shrink-0 border-t px-4 py-3">
        <DataTablePagination
          table={table}
          entityLabelPlural={entityNamePlural}
          pageSizeOptions={pageSizeOptions}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          isServerPaginated={Boolean(table.options.manualPagination)}
          serverRowCount={totalFilteredRowCount}
          serverCanNextPage={table.getCanNextPage()}
        />
      </div>
    </div>
  );
}
